import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, interval, Subscription } from 'rxjs';
import { exhaustMap, first } from 'rxjs/operators';

import { AlertModalService } from '../shared/form-elements/components/alert-modal/alert-modal.service';
import { isAppBoundDomainRejection, ServiceWorkerRegistrationService } from './service-worker-registration.service';

@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {

    isNewVersionAvailable: boolean = false;
    intervalSource = interval(4 * 60 * 60 * 1000); // every 4 hours

    private polling?: Subscription;

    constructor(
        private appRef: ApplicationRef
        , private swUpdate: SwUpdate
        , private zone: NgZone
        , private alertModalService: AlertModalService
        , private swRegistration: ServiceWorkerRegistrationService
    ) { }

    /**
     * Starts the session's one update poll: once the app is stable, then every 4 hours. Later calls
     * are no-ops. S2N-3226: each call used to subscribe again, and every tick nested another 4-hour
     * interval, so checks multiplied the longer the app stayed open.
     */
    public checkForUpdates(): void {
        if (this.polling || !this.swUpdate.isEnabled) { return; }

        // Allow the app to stabilize first, before starting polling for updates with `interval()`.
        const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));

        this.zone.runOutsideAngular(() => {
            this.polling = concat(appIsStable$, this.intervalSource)
                .pipe(exhaustMap(() => this.checkOnce()))
                .subscribe();
        });
    }

    private async checkOnce(): Promise<void> {
        if (this.swRegistration.unsupported) { return; }
        try {
            const isNewVersionAvailable = await this.swUpdate.checkForUpdate();
            this.zone.run(() => {
                this.isNewVersionAvailable = isNewVersionAvailable;
                if (isNewVersionAvailable) { this.promptUser(); }
            });
            console.log(isNewVersionAvailable ? 'A new version is available.' : 'Already on the latest version.');
        } catch (error) {
            if (isAppBoundDomainRejection(error)) { this.swRegistration.handleFailure(error); }
            else { console.error('Failed to check for updates:', error); }
        }
    }

    applyUpdate(): void {
        // Reload the page to update to the latest version after the new version is activated
        this.swUpdate.activateUpdate()
            .then(() => document.location.reload())
            .catch(error => console.error('Failed to apply updates:', error));
    }

    promptUser(): void {
        this.alertModalService.openAlert(
            'A new version of the app is available. Please reload.',
            () => {
                this.swUpdate.activateUpdate()
                    .then(() => window.location.reload())
                    .catch(error => console.error('Failed to apply updates:', error));
            }
        );
    }
}
